import React, { useState, useEffect, useCallback } from 'react';
import { TicTacToeGame, TicTacToeCell } from '../../utils/gameLogic';
import './TicTacToeGame.css';

const TicTacToeGameComponent: React.FC = () => {
  const [game, setGame] = useState(new TicTacToeGame());
  const [board, setBoard] = useState<TicTacToeCell[]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<'X' | 'O'>('X');
  const [winner, setWinner] = useState<'X' | 'O' | 'draw' | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [gameCount, setGameCount] = useState(0);
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 });

  const updateGameState = useCallback(() => {
    setBoard(game.getBoard());
    setCurrentPlayer(game.getCurrentPlayer());
    setWinner(game.getWinner());
    setGameOver(game.getGameOver());
  }, [game]);

  useEffect(() => {
    updateGameState();
  }, [game, updateGameState]);

  const handleCellClick = (index: number) => {
    if (game.onCellClick(index)) {
      updateGameState();
      
      // Check if game is over and update scores
      if (game.getGameOver()) {
        const gameWinner = game.getWinner();
        if (gameWinner === 'X') {
          setScores(prev => ({ ...prev, X: prev.X + 1 }));
        } else if (gameWinner === 'O') {
          setScores(prev => ({ ...prev, O: prev.O + 1 }));
        } else if (gameWinner === 'draw') {
          setScores(prev => ({ ...prev, draws: prev.draws + 1 }));
        }
        setGameCount(prev => prev + 1);
      }
    }
  };

  const resetGame = () => {
    setGame(new TicTacToeGame());
  };

  const getWinnerText = () => {
    if (winner === 'X') return '🎉 X Wins!';
    if (winner === 'O') return '🎉 O Wins!';
    if (winner === 'draw') return '🤝 It\'s a Draw!';
    return `Current Player: ${currentPlayer}`;
  };

  const getWinnerClass = () => {
    if (winner === 'X') return 'x-wins';
    if (winner === 'O') return 'o-wins';
    if (winner === 'draw') return 'draw';
    return '';
  };

  return (
    <div className="tic-tac-toe-game">
      <div className="game-header">
        <h2>⭕ Tic-Tac-Toe Game</h2>
        <p>Get three in a row to win!</p>
      </div>
      
      <div className="game-stats">
        <div className="stat-item">
          <span className="stat-label">Games Played:</span>
          <span className="stat-value">{gameCount}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">X Wins:</span>
          <span className="stat-value">{scores.X}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">O Wins:</span>
          <span className="stat-value">{scores.O}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Draws:</span>
          <span className="stat-value">{scores.draws}</span>
        </div>
      </div>

      <div className={`game-status ${getWinnerClass()}`}>
        <h3>{getWinnerText()}</h3>
      </div>
      
      <div className="tic-tac-toe-grid">
        {board.map((cell, index) => (
          <div
            key={index}
            className={`game-cell ${cell.value ? `filled-${cell.value.toLowerCase()}` : ''} ${gameOver ? 'game-over' : ''}`}
            onClick={() => handleCellClick(index)}
          >
            {cell.value}
          </div>
        ))}
      </div>
      
      <div className="game-controls">
        <button onClick={resetGame} className="reset-button">
          🔄 New Game
        </button>
        <button onClick={() => {
          setScores({ X: 0, O: 0, draws: 0 });
          setGameCount(0);
          resetGame();
        }} className="reset-scores-button">
          📊 Reset Scores
        </button>
      </div>
      
      <div className="game-instructions">
        <h4>How to Play:</h4>
        <ul>
          <li>Players take turns placing X and O</li>
          <li>Get three in a row (horizontal, vertical, or diagonal) to win</li>
          <li>If all squares are filled with no winner, it's a draw</li>
          <li>Click on an empty square to make your move</li>
        </ul>
      </div>
    </div>
  );
};

export default TicTacToeGameComponent;
